import React, { useCallback, useEffect, useState } from 'react';
import { useTournament, TournamentAdmins } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';

interface ManageAdminsDialogProps {
  tournamentId: string;
  tournamentName: string;
  onClose: () => void;
}

// Owner-only dialog (the row action that opens this is itself gated to owned rows) for
// granting/revoking other users owner-equivalent day-to-day access to one tournament. Kept
// as its own independent modal rather than folded into TournamentDialog's settings form —
// unlike every field there, admin add/remove is a live list of individually-async actions
// (each row has its own Remove), not a single form submitted all at once, and it needs to
// work regardless of tournament status, whereas settings editing is registration-only.
export default function ManageAdminsDialog({ tournamentId, tournamentName, onClose }: ManageAdminsDialogProps) {
  const { fetchTournamentAdmins, addTournamentAdmin, removeTournamentAdmin } = useTournament();
  const { t } = useLang();

  const [data, setData] = useState<TournamentAdmins | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    fetchTournamentAdmins(tournamentId)
      .then(setData)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [fetchTournamentAdmins, tournamentId]);

  useEffect(reload, [reload]);

  const handleAdd = async () => {
    if (!username.trim()) return;
    setAdding(true); setError('');
    try {
      const updated = await addTournamentAdmin(tournamentId, username.trim());
      setData(updated);
      setUsername('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add admin');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: string) => {
    setRemovingId(userId); setError('');
    try {
      const updated = await removeTournamentAdmin(tournamentId, userId);
      setData(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove admin');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', animation: 'fadeInAdmins 0.15s ease',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--baize), var(--felt))',
        border: '2px solid rgba(155,89,182,0.4)', borderRadius: '14px',
        padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '440px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUpAdmins 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', lineHeight: 1, color: '#af7ac5' }}>
            👥 {t.adminsBtn}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', marginBottom: '1.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tournamentName}
        </p>

        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', lineHeight: 1.5, marginBottom: '0.9rem' }}>
          {t.adminsDesc}
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
          <input
            value={username}
            onChange={e => setUsername(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder={t.usernamePlaceholder}
            style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none' }}
          />
          <button
            onClick={handleAdd}
            disabled={adding || !username.trim()}
            style={{ background: 'rgba(155,89,182,0.2)', color: '#af7ac5', border: '1px solid rgba(155,89,182,0.4)', borderRadius: '8px', padding: '0.55rem 1rem', fontSize: '0.82rem', fontWeight: 700, cursor: adding || !username.trim() ? 'not-allowed' : 'pointer', opacity: adding || !username.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}
          >
            {adding ? '…' : t.addBtn}
          </button>
        </div>

        {error && (
          <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: '#e74c3c', fontSize: '0.78rem', marginBottom: '0.9rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', textAlign: 'center', padding: '1rem 0' }}>…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '240px', overflowY: 'auto' }}>
            {data?.admins.length === 0 && (
              <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', textAlign: 'center', padding: '0.75rem 0' }}>{t.noAdminsYet}</p>
            )}
            {data?.admins.map(a => (
              <div key={a._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.5rem 0.75rem' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--chalk)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.username}</span>
                <button
                  onClick={() => handleRemove(a._id)}
                  disabled={removingId === a._id}
                  style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '6px', padding: '0.3rem 0.6rem', fontSize: '0.7rem', fontWeight: 600, cursor: removingId === a._id ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                >
                  {removingId === a._id ? '…' : t.removeBtn}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeInAdmins  { from{opacity:0} to{opacity:1} }
        @keyframes slideUpAdmins { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
